import { Link } from 'react-router-dom';
import {
  Cel,
  Kaart,
  KaartKop,
  KnopLink,
  Leeg,
  PaginaKop,
  Plek,
  Rij,
  SkeletRegels,
  SkeletTabel,
  SkeletTegels,
  SpelerCel,
  Tabel,
  Tegel,
  WedstrijdRegel
} from '../components/ui';
import { IcoonPlus } from '../components/icons';
import { useAuth } from '../lib/auth';
import { chronologisch, deeltPlek, huidigSeizoen, plekken, reeksVan, seizoenLabel, seizoensStand, standSleutel } from '../lib/elo';
import { relatief, toonRating } from '../lib/format';
import { useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';

export default function Dashboard() {
  const { pouleId } = usePoule();
  const { players, actief, matches, ranking, elo, zoekSpeler, rating, laden, fout } = useLeague(pouleId);
  const { profile } = useAuth();

  if (fout) {
    return (
      <>
        <PaginaKop titel="Dashboard" />
        <Kaart>
          <Leeg tekst={fout instanceof Error ? fout.message : 'Ophalen mislukte.'} ikoon="verbod" />
        </Kaart>
      </>
    );
  }

  if (laden) {
    return (
      <>
        <PaginaKop titel="Dashboard" onder="Hoe staat het ervoor aan de tafel" />
        <SkeletTegels />
        <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
          <Kaart>
            <KaartKop titel="Ranglijst" />
            <SkeletTabel rijen={6} kolommen={5} />
          </Kaart>
          <Kaart>
            <KaartKop titel="Dit kwartaal" />
            <SkeletTabel rijen={6} kolommen={4} />
          </Kaart>
        </div>
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="Laatste potjes" />
            <SkeletRegels aantal={6} />
          </Kaart>
        </div>
      </>
    );
  }

  const laatste = chronologisch(matches).reverse();
  const seizoen = huidigSeizoen();
  const seizoensRijen = seizoensStand(seizoen, matches, zoekSpeler);

  /* Gelijke rating deelt de plek. Over de hele lijst rekenen en pas daarna
     afkappen, anders klopt het nummer van de zesde niet. */
  const plaatsenAlles = plekken(ranking, standSleutel);
  const plaatsenSeizoen = plekken(seizoensRijen, standSleutel);

  const nu = new Date();
  const dezeMaand = matches.filter((m) => {
    const d = new Date(m.playedAt);
    return d.getMonth() === nu.getMonth() && d.getFullYear() === nu.getFullYear();
  }).length;

  const koploper = ranking[0];
  const mijnSpeler = profile ? players.find((p) => p.id === profile.id) : undefined;

  return (
    <>
      <PaginaKop
        titel="Dashboard"
        onder="Hoe staat het ervoor aan de tafel"
        rechts={
          <KnopLink to="/nieuw" variant="primair">
            <IcoonPlus /> Uitslag invoeren
          </KnopLink>
        }
      />

      <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Tegel
          label="Potjes gespeeld"
          waarde={matches.length}
          onder={laatste.length ? `laatste: ${relatief(laatste[0].playedAt)}` : 'nog niets vastgelegd'}
        />
        <Tegel label="Deze maand" waarde={dezeMaand} onder={nu.toLocaleDateString('nl-NL', { month: 'long' })} />
        <Tegel
          label="Koploper"
          waarde={koploper ? koploper.player.name : '—'}
          onder={koploper ? `${toonRating(koploper.rating)} punten` : ''}
        />
        {mijnSpeler ? (
          <Tegel
            label="Jouw rating"
            waarde={toonRating(rating(mijnSpeler.id))}
            onder={reeksTekst(reeksVan(mijnSpeler.id, matches))}
          />
        ) : (
          <Tegel label="Actieve spelers" waarde={actief.length} onder={`${players.length} in totaal`} />
        )}
      </div>

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
        <Kaart>
          <KaartKop
            titel="Ranglijst"
            hint={
              <Link to="/ranglijst" className="hover:underline">
                alles zien →
              </Link>
            }
          />
          {ranking.length ? (
            <Tabel koppen={['#', 'Speler', 'Rating', 'G–V', 'Winst']}>
              {ranking.slice(0, 6).map((r, i) => (
                <Rij key={r.player.id}>
                  <Cel className="w-8">
                    <Plek nr={plaatsenAlles[i]} gedeeld={deeltPlek(plaatsenAlles, i)} />
                  </Cel>
                  <Cel>
                    <SpelerCel speler={r.player} />
                  </Cel>
                  <Cel nummer>
                    <strong>{toonRating(r.rating)}</strong>
                  </Cel>
                  <Cel nummer>
                    {r.stats.gewonnen}–{r.stats.verloren}
                  </Cel>
                  <Cel nummer>{Math.round(r.stats.winstPct)}%</Cel>
                </Rij>
              ))}
            </Tabel>
          ) : (
            <Leeg tekst="Nog geen spelers." ikoon="spelers" />
          )}
        </Kaart>

        <Kaart>
          <KaartKop titel={seizoenLabel(seizoen)} hint="zachte reset: een kwart van je voorsprong ging mee" />
          {seizoensRijen.length ? (
            <Tabel koppen={['#', 'Speler', 'Rating', 'Potjes']}>
              {seizoensRijen.slice(0, 6).map((r, i) => (
                <Rij key={r.player.id}>
                  <Cel className="w-8">
                    <Plek nr={plaatsenSeizoen[i]} gedeeld={deeltPlek(plaatsenSeizoen, i)} />
                  </Cel>
                  <Cel>
                    <SpelerCel speler={r.player} />
                  </Cel>
                  <Cel nummer>
                    <strong>{toonRating(r.rating)}</strong>
                  </Cel>
                  <Cel nummer>{r.stats.gespeeld}</Cel>
                </Rij>
              ))}
            </Tabel>
          ) : (
            <Leeg tekst="Dit kwartaal is er nog niet gespeeld." ikoon="kalender" />
          )}
        </Kaart>
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Laatste potjes"
            hint={
              <Link to="/historie" className="hover:underline">
                hele historie →
              </Link>
            }
          />
          {laatste.length ? (
            laatste
              .slice(0, 8)
              .map((m) => (
                <WedstrijdRegel key={m.id} potje={m} zoekSpeler={zoekSpeler} delta={elo.perPotje[m.id]?.deltaA} deltaB={elo.perPotje[m.id]?.deltaB} />
              ))
          ) : (
            <Leeg tekst="Nog geen uitslagen. Voer er een in met de knop rechtsboven." ikoon="bal" />
          )}
        </Kaart>
      </div>
    </>
  );
}

function reeksTekst(reeks: { soort: 'winst' | 'verlies' | null; aantal: number }): string {
  if (!reeks.aantal) return 'nog niet gespeeld';
  return `${reeks.aantal} keer op rij ${reeks.soort === 'winst' ? 'gewonnen' : 'verloren'}`;
}
